import { DASHBOARD_STATUSES, type DashboardQuery, type DashboardStatus, type DashboardTab } from './dashboardTypes';
import { PAGE_SIZE_OPTIONS, type DashboardFilters } from './filterModel';

const TABS: readonly DashboardTab[] = ['All', 'Pending', 'Processing', 'Paid', 'NeedsAttention'];
const DATE = /^\d{4}-\d{2}-\d{2}$/;

export interface ListState {
  tab: DashboardTab;
  filters: DashboardFilters;
  page: number;
  pageSize: number;
  payment: string | null;
}

/** The address carries tab, filters, page and the open payment, so a reload, Back or a shared link restores the same view. Anything not valid is ignored. */
export function parseListState(params: URLSearchParams): ListState {
  const tab = params.get('tab');
  const status = params.get('status');
  const form = params.get('form');
  const from = params.get('from');
  const to = params.get('to');
  const page = Number(params.get('page'));
  const size = Number(params.get('size'));
  const payment = params.get('payment');
  return {
    tab: TABS.includes(tab as DashboardTab) ? (tab as DashboardTab) : 'All',
    filters: {
      status: DASHBOARD_STATUSES.includes(status as DashboardStatus) ? (status as DashboardStatus) : '',
      formCode: form === 'Form941' || form === 'Form940' ? form : '',
      dueFrom: from && DATE.test(from) ? from : '',
      dueTo: to && DATE.test(to) ? to : '',
    },
    page: Number.isInteger(page) && page >= 1 ? page : 1,
    pageSize: (PAGE_SIZE_OPTIONS as readonly number[]).includes(size) ? size : PAGE_SIZE_OPTIONS[0],
    payment: payment && /^[A-Za-z0-9-]{1,64}$/.test(payment) ? payment : null,
  };
}

export function listStateToParams(state: ListState): URLSearchParams {
  const params = new URLSearchParams();
  if (state.tab !== 'All') params.set('tab', state.tab);
  if (state.filters.status) params.set('status', state.filters.status);
  if (state.filters.formCode) params.set('form', state.filters.formCode);
  if (state.filters.dueFrom) params.set('from', state.filters.dueFrom);
  if (state.filters.dueTo) params.set('to', state.filters.dueTo);
  if (state.page > 1) params.set('page', String(state.page));
  if (state.pageSize !== PAGE_SIZE_OPTIONS[0]) params.set('size', String(state.pageSize));
  if (state.payment) params.set('payment', state.payment);
  return params;
}

export function toDashboardQuery(state: ListState): DashboardQuery {
  return {
    tab: state.tab,
    ...(state.filters.status ? { status: state.filters.status } : {}),
    ...(state.filters.formCode ? { formCode: state.filters.formCode } : {}),
    ...(state.filters.dueFrom ? { dueFrom: state.filters.dueFrom } : {}),
    ...(state.filters.dueTo ? { dueTo: state.filters.dueTo } : {}),
    page: state.page,
    pageSize: state.pageSize,
  };
}
